import { useState, useEffect } from 'react'
import { Button, Form, Select, DatePicker, Typography, Divider } from 'antd'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import moment from 'moment'
import { getArenaGames, saveEditDailyChallenge } from 'src/api/arena'

const { Title, Text } = Typography

const STATUS_OPTIONS: { label: string; value: ArenaDailyChallengeStatus }[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Disabled', value: 'DISABLED' },
  { label: 'Finished', value: 'FINISHED' },
]

interface GameOption {
  label: string
  value: string
}

interface DailyChallengeFormValues {
  gameId: string
  status: ArenaDailyChallengeStatus
  startAt: moment.Moment
  endAt: moment.Moment
}

const EditDailyChallenge = (): JSX.Element => {
  const { id = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const params = new URLSearchParams(location.search)
  const startAt = params.get('startAt')
  const endAt = params.get('endAt')

  const [gameOptions, setGameOptions] = useState<GameOption[]>([])
  const [saving, setSaving] = useState<boolean>(false)

  useEffect(() => {
    const fetchGames = async (): Promise<void> => {
      const games = await getArenaGames()
      setGameOptions(
        games.map((game) => ({
          label: game.title ?? game.gameId,
          value: game.gameId,
        }))
      )
    }
    fetchGames()
  }, [])

  const handleSave = async (): Promise<void> => {
    const values = (await form.validateFields()) as DailyChallengeFormValues
    setSaving(true)
    const result = await saveEditDailyChallenge(id, {
      gameId: values.gameId,
      status: values.status,
      startAt: values.startAt.toISOString(),
      endAt: values.endAt.toISOString(),
    })
    setSaving(false)
    if (result === 'success') {
      navigate('/arena/daily-challenges')
    }
  }

  const onCancel = (): void => {
    navigate('/arena/daily-challenges')
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <Title level={3}>
        <Text>Edit Daily Challenge</Text>
      </Title>
      <Divider />
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Form.Item
          label="Game"
          name="gameId"
          initialValue={params.get('gameId') ?? undefined}
          rules={[{ required: true, message: 'Please select a game' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select a game"
            options={gameOptions}
          />
        </Form.Item>
        <Form.Item
          label="Start At"
          name="startAt"
          initialValue={startAt ? moment(startAt) : undefined}
          rules={[{ required: true, message: 'Please select a start date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
        </Form.Item>
        <Form.Item
          label="End At"
          name="endAt"
          initialValue={endAt ? moment(endAt) : undefined}
          rules={[{ required: true, message: 'Please select an end date' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
        </Form.Item>
        <Form.Item
          label="Status"
          name="status"
          initialValue={params.get('status') ?? undefined}
          rules={[{ required: true, message: 'Please select a status' }]}
        >
          <Select placeholder="Select a status" options={STATUS_OPTIONS} />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 6 }}>
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            style={{ marginRight: 12 }}
          >
            Save
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Form.Item>
      </Form>
    </div>
  )
}

EditDailyChallenge.displayName = 'EditDailyChallenge'

export default EditDailyChallenge
